import React from 'react'
import { cn } from '@/lib/utils'
import { Spinner } from './Spinner'

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline'
type ButtonSize    = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:  ButtonVariant
  size?:     ButtonSize
  loading?:  boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: [
    'bg-primary-600 text-white',
    'hover:bg-primary-700 active:bg-primary-800',
    'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
    'disabled:bg-primary-300',
    'shadow-sm',
  ].join(' '),

  secondary: [
    'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    'hover:bg-slate-200 dark:hover:bg-slate-600 active:bg-slate-300 dark:active:bg-slate-500',
    'focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2',
    'disabled:bg-slate-50 disabled:text-slate-400',
  ].join(' '),

  danger: [
    'bg-sport-red text-white',
    'hover:bg-red-600 active:bg-red-700',
    'focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
    'disabled:bg-red-300',
    'shadow-sm',
  ].join(' '),

  ghost: [
    'bg-transparent text-slate-700 dark:text-slate-300',
    'hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600',
    'focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2',
    'disabled:text-slate-300',
  ].join(' '),

  outline: [
    'bg-transparent text-primary-600 dark:text-primary-400',
    'border border-primary-600 dark:border-primary-400',
    'hover:bg-primary-50 dark:hover:bg-primary-900/20 active:bg-primary-100',
    'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
    'disabled:text-primary-300 disabled:border-primary-200',
  ].join(' '),
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2.5',
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant  = 'primary',
      size     = 'md',
      loading  = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        className={cn(
          // Base
          'inline-flex items-center justify-center',
          'font-medium rounded-lg',
          'transition-colors duration-150',
          'cursor-pointer select-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'focus:outline-none',
          // Variant + size
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className,
        )}
        {...props}
      >
        {loading ? (
          <Spinner
            size={size === 'lg' ? 'md' : 'sm'}
            className={cn(
              variant === 'primary' || variant === 'danger'
                ? 'text-white/70'
                : 'text-current',
            )}
          />
        ) : (
          leftIcon && (
            <span aria-hidden="true" className="flex-shrink-0">
              {leftIcon}
            </span>
          )
        )}

        {children && <span>{children}</span>}

        {!loading && rightIcon && (
          <span aria-hidden="true" className="flex-shrink-0">
            {rightIcon}
          </span>
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'
